import type { IDeal } from './IDeal';
import type { IPointOfInterest } from './IPointOfInterest';

/** Where a creation run stands, as `GET /locations/{location_id}/deal` reports it. */
export type DealCreationStatus = 'running' | 'created' | 'failed';

/**
 * The step a run is on, or the one it stopped at. It names the place to look, never a partial
 * state, and it is the step the form returns the BD to after a failure.
 */
export type DealCreationStep = 'company' | 'contact' | 'deal' | 'persist';

/**
 * One reading of a creation run. `deal` is present only with `created`, so the frame that says the
 * deal exists is also the one that renders it.
 */
export interface IDealCreationProgress {
  status: DealCreationStatus;
  step?: DealCreationStep;
  deal_id?: string;
  deal?: IDeal;
  /** What the BD can act on, never the CRM's own wording. */
  message?: string;
}

/** A company to create, pre-filled from the location and correctable. */
export interface ICompanyDraft {
  name: string;
  street?: string;
  postcode?: string;
  city?: string;
  country?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

/** A contact to create. Its type and country are the system's and have no field. */
export interface IContactDraft {
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  job_title?: string;
}

/**
 * What `POST /locations/{location_id}/deal` takes. Only FR-024's editable fields are here: the
 * pipeline, the stage, the deal's coordinates, the country and the SpotOn link are the system's,
 * and sending one is rejected rather than ignored.
 *
 * A company and a contact are each either attached by id or created, never both.
 */
export interface ICreateLocationDealRequest {
  deal_name: string;
  owner_email?: string;
  zone_ref?: string;
  company_id?: string;
  company?: ICompanyDraft;
  contact_id?: string;
  contact?: IContactDraft;
  /**
   * The place the BD picked for the deal. A location with no place of its own is linked to it
   * before the run starts; one that already carries a place keeps it.
   */
  point_of_interest?: IPointOfInterest;
  /**
   * The site's business category. Absent leaves it unset. A linked place whose icon decides it
   * prefills it; one that needs the surroundings does not.
   */
  landowner_type?: LandownerType;
}

/**
 * The site categories a deal can carry, in the order the CRM lists them. The first icons that
 * decide a category are Airport, Gas station, Hotel, Fast food restaurant and Parking (with
 * payment); the rest need the surroundings.
 */
export const LANDOWNER_TYPES = [
  'Airport',
  'Gas station',
  'Hotel',
  'Retail others',
  'Shopping mall',
  'Retail food - supermarket',
  'Retail food - hypermarket',
  'Fast food restaurant',
  'Rest area',
  'Retail park',
  'Service area',
  'Parking (with payment)',
  'Other',
] as const;

export type LandownerType = (typeof LANDOWNER_TYPES)[number];

/** A company the CRM search matched. */
export interface IHubSpotCompany {
  id: string;
  name: string;
  address?: string;
  city?: string;
}

/** A contact the CRM holds for an exact email. */
export interface IHubSpotContact {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  job_title?: string;
}

/** A CRM user an email resolved to. */
export interface IHubSpotOwner {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

/** The CRM users a deal can be assigned to, sorted by email. */
export interface IHubSpotOwnerList {
  owners: IHubSpotOwner[];
}

/**
 * One hunting zone offered for the deal. `is_inside` marks the point as inside the polygon, and
 * several candidates can carry it at once since polygons may overlap.
 */
export interface IZoneCandidate {
  id: string;
  name: string;
  owner_id?: string | null;
  is_inside: boolean;
  /** Distance from the point to the polygon, in metres. 0 when `is_inside`. */
  distance: number;
}
